import { Box, CircularProgress, DialogContent, Divider, Grid, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import Avatar from '@hapana/shared/web/components/shared/avatar/avatar.component'
import {
  AVATAR_SIZE,
  AVATAR_TYPES,
} from '@hapana/shared/web/components/shared/avatar/avatar.constants'
import DialogLayout from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.component'
import {
  CLOSE_TYPES,
  SIZES,
} from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.constants'
import AgreementsIndividualAlert from '@hapana/shared/web/components/shared/individual-alerts/agreements-individual-alert/agreements-individual-alert.component'
import AlertSection from '@hapana/shared/web/components/shared/individual-alerts/alert-section/alert-section.component'
import BirthdayAlert from '@hapana/shared/web/components/shared/individual-alerts/birthday-alert/birthday-alert.component'
import FirstSessionAlert from '@hapana/shared/web/components/shared/individual-alerts/first-session-alert/first-session-alert.component'
import FormsPendingIndividualAlert from '@hapana/shared/web/components/shared/individual-alerts/forms-pending-individual-alert/forms-pending-individual-alert.component'
import IntroOffersAlert from '@hapana/shared/web/components/shared/individual-alerts/intro-offers-alert/intro-offers-alert.component'
import LocationAlert from '@hapana/shared/web/components/shared/individual-alerts/location-alert/location-alert.component'
import PackagesIndividualAlert from '@hapana/shared/web/components/shared/individual-alerts/packages-individual-alert/packages-individual-alert.component'
import PaymentsIndividualAlert from '@hapana/shared/web/components/shared/individual-alerts/payments-alert/payments-alert.component'
import SuspendedAlert from '@hapana/shared/web/components/shared/individual-alerts/suspended-alert/suspended-alert.component'
import Tabs from '@hapana/shared/web/components/shared/tabs/horizontal-tabs/tabs.component'
import {
  useClientActivePackages,
  useClientFailedPayments,
  useClientNotesAndProperties,
  useClientPendingAgreements,
} from '@hapana/shared/web/hooks/client.hooks'
import { useSiteIdResolved } from '@hapana/shared/web/hooks/site.hooks'
import { isBirthdayWithinWeek } from '@hapana/shared/utils/date.util'

import { styles } from './modal-alerts.styles'
import { TABS_VALUES } from './notes-properties/modal-alerts.constants'
import { NOTE_ALERT_TYPES } from './notes-properties/notes-properties.constants'
import NotesType from './notes-properties/notes-type.component'

const ModalAlerts = ({
  v1ClientId,
  profileImg,
  name,
  firstName,
  failedPayments,
  formsPending,
  pendingAgreements,
  homeLocations,
  birthday,
  isFirstSession,
  introOffer,
  suspendedClient,
  clientId,
  tabDefaultValue,
  hasNotesAndProperties,
  location,
  notesAndPropertiesData,
  activePackagesData,
  packagesCount,
  closeAction,
}) => {
  const { t } = useTranslation(['checkIn', 'alerts'])
  const siteId = useSiteIdResolved()
  const { data, isLoading: isNotesLoading } = useClientNotesAndProperties(clientId)
  const { data: packagesData, isLoading: isPackagesLoading } = useClientActivePackages(clientId)
  const { data: fetchedAgreements, isLoading: isAgreementsLoading } =
    useClientPendingAgreements(clientId)
  const { data: fetchedPayments, isLoading: isPaymentsLoading } = useClientFailedPayments(clientId)
  const notesAndProperties = notesAndPropertiesData ?? data
  const packages = activePackagesData ?? packagesData
  // Props win over self-fetched data (backwards compat with check-in widget callers)
  // Stubs from AlertIconsRow only have { id } — detect and replace with fetched data
  const isStub = (arr) => arr?.length > 0 && !arr[0]?.price && !arr[0]?.label
  const resolvedAgreements = pendingAgreements?.length > 0 ? pendingAgreements : fetchedAgreements
  const resolvedFailedPayments = isStub(failedPayments)
    ? fetchedPayments.failedPayments?.length > 0
      ? fetchedPayments.failedPayments
      : failedPayments
    : (failedPayments ?? [])
  const areNotesLoading = notesAndPropertiesData ? false : isNotesLoading
  const arePackagesLoading = activePackagesData ? false : isPackagesLoading
  const areAgreementsLoading = pendingAgreements?.length > 0 ? false : isAgreementsLoading
  const isLoading =
    arePackagesLoading || areNotesLoading || areAgreementsLoading || isPaymentsLoading
  const hasPackages = packages?.clientSiteActivePackages?.length > 0 || packagesCount > 0
  const isHomeLocation = !homeLocations || homeLocations.length === 0
  const showBirthday = birthday && isBirthdayWithinWeek(birthday)
  const hasGeneralAlerts =
    resolvedFailedPayments?.length > 0 ||
    hasPackages ||
    formsPending?.length > 0 ||
    resolvedAgreements?.length > 0 ||
    (homeLocations ?? false) ||
    showBirthday ||
    isFirstSession ||
    (introOffer ?? false) ||
    (suspendedClient ?? false)

  const totalAlertsCount =
    (resolvedFailedPayments?.length > 0 ? 1 : 0) +
    (hasPackages ? 1 : 0) +
    (formsPending?.length > 0 ? 1 : 0) +
    (resolvedAgreements?.length > 0 ? 1 : 0) +
    (notesAndProperties?.clientSiteNotes?.length > 0 ||
    notesAndProperties?.clientSiteAlertedProperties?.length > 0
      ? 1
      : 0) +
    (introOffer ? 1 : 0) +
    (isFirstSession ? 1 : 0) +
    (suspendedClient ? 1 : 0) +
    (showBirthday ? 1 : 0) +
    (homeLocations?.length > 0 ? 1 : 0)

  const showTabs = () => (
    <Tabs
      tabs={[
        {
          label: t('checkIn:general'),
          value: TABS_VALUES[0],
          content: (
            <DialogContent sx={styles.generalAlertsWrapperInTabs}>
              {generalAlertsContent()}
            </DialogContent>
          ),
        },
        {
          label: t('checkIn:notesAndProperties'),
          value: TABS_VALUES[1],
          content: (
            <DialogContent sx={styles.generalAlertsWrapperInTabs}>
              {notesAndPropertiesContent()}
            </DialogContent>
          ),
        },
      ]}
      defaultValue={tabDefaultValue}
    />
  )

  const generalAlertsContent = () => {
    return (
      <Box sx={styles.selectsSection}>
        {resolvedFailedPayments?.length > 0 && (
          <>
            <PaymentsIndividualAlert
              failedPayments={resolvedFailedPayments}
              siteId={siteId}
              isHomeLocation={isHomeLocation}
            />
            <Divider sx={styles.divider} />
          </>
        )}
        {resolvedAgreements?.length > 0 && (
          <>
            <AgreementsIndividualAlert agreements={resolvedAgreements} v1ClientId={v1ClientId} />
            <Divider sx={styles.divider} />
          </>
        )}
        {formsPending?.length > 0 && (
          <>
            <FormsPendingIndividualAlert formsPending={formsPending} />
            <Divider sx={styles.divider} />
          </>
        )}
        {homeLocations?.length > 0 && (
          <>
            <LocationAlert homeLocations={homeLocations} />
            <Divider sx={styles.divider} />
          </>
        )}
        {showBirthday && (
          <>
            <BirthdayAlert birthday={birthday} />
            <Divider sx={styles.divider} />
          </>
        )}
        {hasPackages && (
          <>
            {packages?.clientSiteActivePackages?.length > 0 ? (
              <PackagesIndividualAlert packages={packages.clientSiteActivePackages} />
            ) : (
              <AlertSection
                type="packages"
                title={t('alerts:packages.title')}
                count={packagesCount}
              />
            )}
            <Divider sx={styles.divider} />
          </>
        )}
        {isFirstSession && (
          <>
            <FirstSessionAlert firstName={firstName} />
            <Divider sx={styles.divider} />
          </>
        )}
        {introOffer && (
          <>
            <IntroOffersAlert location={location} />
            <Divider sx={styles.divider} />
          </>
        )}
        {suspendedClient && <SuspendedAlert suspendedDate={suspendedClient} />}
      </Box>
    )
  }

  const notesAndPropertiesContent = () => (
    <>
      <Box sx={styles.notesTypeWrapper}>
        <NotesType
          type={NOTE_ALERT_TYPES.NOTE}
          alerts={notesAndProperties?.clientSiteNotes || []}
          username={name}
        />
      </Box>
      <Divider />
      <Box sx={styles.notesTypeWrapper}>
        <NotesType
          type={NOTE_ALERT_TYPES.PROPERTIES}
          alerts={notesAndProperties?.clientSiteAlertedProperties || []}
          username={name}
        />
      </Box>
    </>
  )

  const displayContent = () => {
    if (hasGeneralAlerts && hasNotesAndProperties) {
      return showTabs()
    }
    if (hasGeneralAlerts && !hasNotesAndProperties) {
      return (
        <DialogContent sx={styles.generalAlertsWrapper}>{generalAlertsContent()}</DialogContent>
      )
    }
    if (hasNotesAndProperties && !hasGeneralAlerts) {
      return (
        <DialogContent sx={styles.generalAlertsWrapper}>
          {notesAndPropertiesContent()}
        </DialogContent>
      )
    }
  }

  return (
    <DialogLayout
      closeType={CLOSE_TYPES.ICON}
      size={SIZES.SM}
      headerStylesOverride={styles.dialogHeader}
      closeAction={closeAction}
      title={
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <Avatar
              item={{ src: profileImg?.url, alt: t('alerts:avatarAlt', { name }) }}
              avatarType={AVATAR_TYPES.SINGLE_AVATAR}
              size={AVATAR_SIZE.SMALL}
            />
          </Grid>
          <Grid item>
            <Typography color="gray.80" typography="header3">
              {name} • {t('checkIn:totalAlerts', { count: totalAlertsCount })}
            </Typography>
          </Grid>
        </Grid>
      }
    >
      {isLoading ? (
        <Box sx={styles.loading}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          <Divider sx={styles.divider} />
          <DialogContent sx={styles.contentWrapper}>{displayContent()}</DialogContent>
        </>
      )}
    </DialogLayout>
  )
}

ModalAlerts.propTypes = {
  profileImg: PropTypes.shape({
    url: PropTypes.string,
  }),
  name: PropTypes.string,
  firstName: PropTypes.string,
  failedPayments: PropTypes.array,
  packages: PropTypes.array,
  formsPending: PropTypes.array,
  pendingAgreements: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      date: PropTypes.string,
    })
  ),
  homeLocations: PropTypes.arrayOf(PropTypes.string),
  birthday: PropTypes.string,
  isFirstSession: PropTypes.bool,
  introOffer: PropTypes.string,
  suspendedClient: PropTypes.string,
  v1ClientId: PropTypes.number,
  hasNotesAndProperties: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      siteId: PropTypes.string,
    }),
  ]),
  clientId: PropTypes.string,
  tabDefaultValue: PropTypes.string,
  totalAlertsCount: PropTypes.number,
  location: PropTypes.string,
  notesAndPropertiesData: PropTypes.shape({
    clientSiteNotes: PropTypes.array,
    clientSiteAlertedProperties: PropTypes.array,
  }),
  activePackagesData: PropTypes.shape({
    clientSiteActivePackages: PropTypes.array,
  }),
  packagesCount: PropTypes.number,
  closeAction: PropTypes.func,
}

ModalAlerts.defaultProps = {
  tabDefaultValue: TABS_VALUES[0],
  notesAndPropertiesData: null,
  activePackagesData: null,
}

export default ModalAlerts
